import * as React from 'react';

import {
    createPalette,
    DOMText,
    PalettePositionWidth,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { TextRange } from '@gitbook/slate';
import type { MarkProps } from '@gitbook/slate-react';
import { useTimeout } from '@gitbook/util-react-hooks';

import { useInlineCommands } from '../useInlineCommands';

/**
 * Component being rendered around the temporary mark created when typing "/".
 */
export function InlineControlsPicker(props: MarkProps): React.ReactElement {
    const { mark, node, offset, attributes, children, editor } = props;
    const { value } = mark.data.toObject();

    const anchorRef = React.useRef<HTMLElement>(null);

    // The inline node will be inserted at this particular range in the document.
    const range = TextRange.create({
        anchorKey: node.key,
        anchorOffset: offset,
        focusKey: node.key,
        focusOffset: offset + 1,
    });

    const inlineCommands = useInlineCommands({ anchorRef, range, editor });

    const paletteHandle = usePaletteHandle(anchorRef, {
        onClose: () => {
            paletteHandle.close();
            editor.change((change) => change.focus());
        },
    });

    useTimeout(() => {
        /**
         * Open the palette only if the editor is focused otherwise if the editor re-renders
         * and the latest selection is around the "/" mark, the palette will be opened unexpectedly.
         */
        if (!paletteHandle.opened && editor.value.isFocused) {
            /**
             * Open the palette with slight delay to avoid opening it instantly.
             *
             * This is useful when the user is typing "/" with no intention to add
             * an inline control so this prevents the instantenous palette flashing.
             */
            paletteHandle.open(
                createPalette({
                    search: value.length > 2 ? { placeholder: 'Insert…' } : undefined, // Only show search if more than 2 values to choose from
                    position: {
                        preferredPlacement: 'left',
                        width: PalettePositionWidth.MEDIUM,
                    },
                    groups: [
                        {
                            key: 'inline-controls',
                            commands: inlineCommands.filter((command) =>
                                value.includes(command.key)
                            ),
                        },
                    ],
                })
            );
        }
    }, 150);

    return (
        <DOMText ref={anchorRef} {...attributes}>
            {children}
        </DOMText>
    );
}
